import { UNIT_SYSTEM } from "constants/units.contants";
import { Group, PalletRow } from "models/packingList";
import { Unit } from "models/units";

export const CALCULATION_HISTORY_STORAGE_KEY = "clo-calculation-history";
export const CALCULATION_HISTORY_MAX_ITEMS = 20;
export const CALCULATION_HISTORY_UPDATED_EVENT = "clo-calculation-history-updated";

export type CalculationHistoryGroupSummary = {
  id: string;
  name: string;
  cargoesCount: number;
};

export type CalculationHistoryEntry = {
  id: string;
  createdAt: number;
  date: string;
  fingerprint: string;
  groups: Group[];
  customPallets: PalletRow[];
  unitSystem: Unit;
  summary: CalculationHistoryGroupSummary[];
};

export type CalculationHistorySnapshot = {
  groups: Group[];
  customPallets: PalletRow[];
  unitSystem: Unit;
};

const stripIds = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stripIds);
  }

  if (value && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      if (key === "id") continue;
      result[key] = stripIds(nested);
    }
    return result;
  }

  return value;
};

export const getCalculationFingerprint = (snapshot: CalculationHistorySnapshot): string =>
  JSON.stringify(
    stripIds({
      groups: snapshot.groups,
      customPallets: snapshot.customPallets,
      unitSystem: snapshot.unitSystem,
    })
  );

export const formatHistoryDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day} ${month}, ${year} · ${hours}:${minutes}`;
};

export const buildHistorySummary = (groups: Group[]): CalculationHistoryGroupSummary[] =>
  groups.map((group) => ({
    id: group.id,
    name: group.name,
    cargoesCount: group.cargoRows.length,
  }));

const isUnit = (value: unknown): value is Unit => value === UNIT_SYSTEM.METRIC || value === UNIT_SYSTEM.IMPERIAL;

const isHistoryEntry = (value: unknown): value is CalculationHistoryEntry => {
  if (!value || typeof value !== "object") return false;
  const entry = value as Partial<CalculationHistoryEntry>;
  return (
    typeof entry.id === "string" &&
    typeof entry.createdAt === "number" &&
    typeof entry.fingerprint === "string" &&
    Array.isArray(entry.groups) &&
    Array.isArray(entry.customPallets) &&
    isUnit(entry.unitSystem)
  );
};

export const readCalculationHistory = (): CalculationHistoryEntry[] => {
  try {
    const raw = localStorage.getItem(CALCULATION_HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isHistoryEntry);
  } catch {
    return [];
  }
};

const writeCalculationHistory = (entries: CalculationHistoryEntry[]) => {
  localStorage.setItem(CALCULATION_HISTORY_STORAGE_KEY, JSON.stringify(entries));
  try {
    window.dispatchEvent(new Event(CALCULATION_HISTORY_UPDATED_EVENT));
  } catch {
    // ignore when window is unavailable
  }
};

export const saveCalculationHistoryEntry = (snapshot: CalculationHistorySnapshot): CalculationHistoryEntry[] => {
  const fingerprint = getCalculationFingerprint(snapshot);
  const existing = readCalculationHistory();

  if (existing.some((entry) => entry.fingerprint === fingerprint)) {
    return existing;
  }

  const createdAt = Date.now();
  const nextEntry: CalculationHistoryEntry = {
    id: `history-${createdAt}-${Math.random().toString(36).slice(2, 9)}`,
    createdAt,
    date: formatHistoryDate(createdAt),
    fingerprint,
    groups: structuredClone(snapshot.groups),
    customPallets: structuredClone(snapshot.customPallets),
    unitSystem: snapshot.unitSystem,
    summary: buildHistorySummary(snapshot.groups),
  };

  const next = [nextEntry, ...existing].slice(0, CALCULATION_HISTORY_MAX_ITEMS);
  writeCalculationHistory(next);
  return next;
};

export const clearCalculationHistory = (): void => {
  writeCalculationHistory([]);
};
